import axios from 'axios';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useLocation } from 'react-router-dom';
import Back from '../../bar';

import {
    Add,
    Cake,
    CalendarMonth,
    CreditCard,
    Delete,
    EmojiEvents,
    Person,
    Phone,
    Tag,
    Wc,
} from '@mui/icons-material';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    InputAdornment,
    Pagination,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';

const API = process.env.REACT_APP_API;

const InfoRow = ({ icon, label, value }) => (
    <Stack direction='row' alignItems='center' spacing={1.5} sx={{ py: 1 }}>
        <Box sx={{ color: 'text.secondary', display: 'flex' }}>{icon}</Box>
        <Box flexGrow={1}>
            <Typography fontSize={11} color='text.secondary' lineHeight={1.2}>{label}</Typography>
            <Typography fontSize={14} fontWeight={500}>{value || '—'}</Typography>
        </Box>
    </Stack>
);

const fmt = (dateStr) =>
    dateStr
        ? new Date(dateStr).toLocaleDateString(undefined, { day: '2-digit', month: 'long', year: 'numeric' })
        : '—';

export default function ReferrerDetails() {
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const referrerId = params.get('referrer_id') || '';
    const studentId = params.get('student_id') || '—';

    const [r, setR] = useState(null);
    const [loading, setLoading] = useState(true);
    const [totalCoins, setTotalCoins] = useState(0);
    const [coins, setCoins] = useState('');
    const [adding, setAdding] = useState(false);
    const [rewards, setRewards] = useState([]);
    const [rewardsLoading, setRewardsLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [balanceSummary, setBalanceSummary] = useState(null);

    // Redemption request state
    const [redeemAmount, setRedeemAmount] = useState('');
    const [redeeming, setRedeeming] = useState(false);
    const [confirmRedeem, setConfirmRedeem] = useState(false);
    const [redeemRequest, setRedeemRequest] = useState(null);
    const [redemptionsLoading, setRedemptionsLoading] = useState(false);

    // Confirm dialogs
    const [confirmAdd, setConfirmAdd] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState({ open: false, rewardid: null });
    const [deleting, setDeleting] = useState(false);
    const [confirmAccept, setConfirmAccept] = useState(false);
    const [confirmRevoke, setConfirmRevoke] = useState(false);
    const [actioning, setActioning] = useState(false);

    useEffect(() => {
        if (!referrerId) { setLoading(false); return; }
        const fetchReferrer = async () => {
            setLoading(true);
            try {
                const formData = new FormData();
                formData.append('studentid', referrerId);
                const res = await axios.post(`${API}/franchise/student/details`, formData);
                if (res.data.status === true || res.data.status === 'success') {
                    setR(res.data.data);
                    setTotalCoins(res.data.data?.coins || 0);
                } else {
                    toast.error(res.data.message || 'Failed to load referrer details');
                }
            } catch (e) {
                toast.error('Failed to load referrer details');
            } finally {
                setLoading(false);
            }
        };
        fetchReferrer();
    }, [referrerId]);

    useEffect(() => {
        if (!referrerId) return;
        fetchRewards(page);
    }, [referrerId, page]);

    useEffect(() => {
        if (!referrerId) return;
        fetchRedemptions();
    }, [referrerId]);

    const fetchRewards = async (p = 1) => {
        setRewardsLoading(true);
        try {
            const res = await axios.get(`${API}/franchise/reward/getall?page=${p}&studentid=${referrerId}`);
            if (res.data.status === true || res.data.status === 'success') {
                setRewards(res.data.data || []);
                setTotalPages(res.data.pagination?.total_pages || 1);
                if (res.data.balance_summary) setBalanceSummary(res.data.balance_summary);
            }
        } catch (e) {
            // silently fail
        } finally {
            setRewardsLoading(false);
        }
    };

    const fetchRedemptions = async () => {
        setRedemptionsLoading(true);
        try {
            const formData = new FormData();
            formData.append('studentid', referrerId);
            const res = await axios.post(`${API}/franchise/reward/request`, formData);
            if (res.data.status === true || res.data.status === 'success') {
                setRedeemRequest(res.data.data || null);
            } else {
                setRedeemRequest(null);
            }
        } catch (e) {
            setRedeemRequest(null);
        } finally {
            setRedemptionsLoading(false);
        }
    };

    const initials = r?.name
        ? r.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
        : '?';

    // ── Add coins ──────────────────────────────────────────────
    const handleAddClick = () => {
        const amount = Number(coins);
        if (!coins || isNaN(amount) || amount <= 0) {
            toast.error('Enter a valid coin amount');
            return;
        }
        setConfirmAdd(true);
    };

    const handleConfirmAdd = async () => {
        setConfirmAdd(false);
        const amount = Number(coins);
        setAdding(true);
        try {
            const formData = new FormData();
            formData.append('studentid', referrerId);
            formData.append('reward_from', studentId);
            formData.append('reward_value', amount);
            const res = await axios.post(`${API}/franchise/reward/add`, formData);
            if (res.data.status === true || res.data.status === 'success') {
                toast.success(res.data.message || `${amount} coins added successfully`);
                setCoins('');
                setTotalCoins(prev => prev + amount);
                setPage(1);
                fetchRewards(1);
            } else {
                toast.error(res.data.message || 'Failed to add coins');
            }
        } catch (e) {
            toast.error('Failed to add coins');
        } finally {
            setAdding(false);
        }
    };

    // ── Delete reward ───────────────────────────────────────────
    const handleDeleteClick = (rewardid) => {
        setConfirmDelete({ open: true, rewardid });
    };

    const handleConfirmDelete = async () => {
        const { rewardid } = confirmDelete;
        setConfirmDelete({ open: false, rewardid: null });
        setDeleting(true);
        try {
            const formData = new FormData();
            formData.append('rewardid', rewardid);
            const res = await axios.post(`${API}/franchise/reward/delete`, formData);
            if (res.data.status === true || res.data.status === 'success') {
                toast.success(res.data.message || 'Reward deleted');
                setPage(1);
                fetchRewards(1);
            } else {
                toast.error(res.data.message || 'Failed to delete reward');
            }
        } catch (e) {
            toast.error('Failed to delete reward');
        } finally {
            setDeleting(false);
        }
    };

    // ── Redemption request ──────────────────────────────────────
    const handleRedeemClick = () => {
        const amount = Number(redeemAmount);
        if (!redeemAmount || isNaN(amount) || amount <= 0) {
            toast.error('Enter a valid redemption amount');
            return;
        }
        const currentBalance = balanceSummary?.balance ?? totalCoins;
        if (amount > currentBalance) {
            toast.error('Redemption amount cannot exceed current balance');
            return;
        }
        setConfirmRedeem(true);
    };

    const handleConfirmRedeem = async () => {
        setConfirmRedeem(false);
        const amount = Number(redeemAmount);
        setRedeeming(true);
        try {
            const formData = new FormData();
            formData.append('studentid', referrerId);
            formData.append('amount', amount);
            const res = await axios.post(`${API}/franchise/reward/redemption/request`, formData);
            if (res.data.status === true || res.data.status === 'success') {
                toast.success(res.data.message || `Redemption request for ${amount} coins submitted`);
                setRedeemAmount('');
                fetchRedemptions();
                fetchRewards(page);
            } else {
                toast.error(res.data.message || 'Failed to submit redemption request');
            }
        } catch (e) {
            toast.error('Failed to submit redemption request');
        } finally {
            setRedeeming(false);
        }
    };

    // ── Accept / Revoke redemption ──────────────────────────────
    const handleAccept = async () => {
        setConfirmAccept(false);
        setActioning(true);
        try {
            const formData = new FormData();
            formData.append('rewardid', redeemRequest?.rewardid ?? '');
            formData.append('reward_status', 'approved');
            const res = await axios.post(`${API}/franchise/reward/status`, formData);
            if (res.data.status === true || res.data.status === 'success') {
                toast.success(res.data.message || 'Redemption request accepted');
                fetchRedemptions();
                fetchRewards(page);
            } else {
                toast.error(res.data.message || 'Failed to accept redemption');
            }
        } catch (e) {
            toast.error('Failed to accept redemption');
        } finally {
            setActioning(false);
        }
    };

    const handleRevoke = async () => {
        setConfirmRevoke(false);
        setActioning(true);
        try {
            const formData = new FormData();
            formData.append('rewardid', redeemRequest?.rewardid ?? '');
            formData.append('reward_status', 'rejected');
            const res = await axios.post(`${API}/franchise/reward/status`, formData);
            if (res.data.status === true || res.data.status === 'success') {
                toast.success(res.data.message || 'Redemption request revoked');
                fetchRedemptions();
                fetchRewards(page);
            } else {
                toast.error(res.data.message || 'Failed to revoke redemption');
            }
        } catch (e) {
            toast.error('Failed to revoke redemption');
        } finally {
            setActioning(false);
        }
    };

    if (loading) {
        return (
            <Box className='content' sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!r) {
        return (
            <Box className='content'>
                <Container>
                    <Back text='Referrer Details' />
                    <Alert severity='warning'>Referrer not found.</Alert>
                </Container>
            </Box>
        );
    }

    return (
        <Box className='content'>
            <Container>
                <Back text='Referrer Details' />

                {/* Profile Banner */}
                <Box sx={{ borderRadius: 3, background: 'linear-gradient(135deg, #0E3A43 0%, #1a6a78 100%)', p: { xs: 2.5, md: 3.5 }, mb: 3, color: '#fff', position: 'relative', overflow: 'hidden' }}>
                    <Box sx={{ position: 'absolute', right: -40, top: -40, width: 180, height: 180, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                    <Box sx={{ position: 'absolute', right: 40, bottom: -60, width: 120, height: 120, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.04)' }} />
                    <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} spacing={2.5}>
                        <Avatar sx={{ width: 72, height: 72, bgcolor: 'rgba(255,255,255,0.18)', fontSize: 26, fontWeight: 700, border: '2px solid rgba(255,255,255,0.4)' }}>
                            {initials}
                        </Avatar>
                        <Box flexGrow={1}>
                            <Typography fontSize={22} fontWeight={700} lineHeight={1.2}>{r.name}</Typography>
                            <Stack direction='row' flexWrap='wrap' gap={1} mt={1}>
                                <Chip icon={<Tag sx={{ color: '#fff !important', fontSize: 14 }} />} label={r.rollno || referrerId} size='small' sx={{ bgcolor: 'rgba(255,255,255,0.14)', color: '#fff', fontSize: 11 }} />
                                <Chip label={r.status} size='small' sx={{ bgcolor: r.status === 'active' ? '#66bb6a' : '#ffa726', color: '#fff', fontWeight: 600, fontSize: 11, textTransform: 'capitalize' }} />
                            </Stack>
                        </Box>
                        {/* Current Balance */}
                        <Box sx={{ textAlign: 'center', bgcolor: 'rgba(102,187,106,0.22)', borderRadius: 2.5, px: 2.5, py: 1.5, border: '1px solid rgba(102,187,106,0.4)', minWidth: 90 }}>
                            <EmojiEvents sx={{ color: '#A5D6A7', fontSize: 24 }} />
                            <Typography fontSize={20} fontWeight={700} lineHeight={1}>{balanceSummary?.balance ?? totalCoins}</Typography>
                            <Typography fontSize={10} sx={{ opacity: 0.85 }}>Balance</Typography>
                        </Box>
                    </Stack>
                </Box>

                {/* Redemption Request Section */}
                <Card sx={{ borderRadius: 3, mb: 3, border: '1px solid #e0e0e0' }}>
                    <CardContent>
                        <Stack direction='row' alignItems='center' spacing={1} mb={1}>
                            <EmojiEvents sx={{ color: '#1565c0' }} />
                            <Typography fontWeight={700} fontSize={14} color='text.secondary'>REDEMPTION REQUEST</Typography>
                        </Stack>
                        <Divider sx={{ mb: 2 }} />

                        {/* Latest Redemption Request */}
                        {redemptionsLoading ? (
                            <Box sx={{ textAlign: 'center', py: 2 }}><CircularProgress size={22} /></Box>
                        ) : redeemRequest ? (() => {
                            const req = redeemRequest;
                            const status = req.reward_status ?? 'request';
                            const statusColor = status === 'approved' ? 'success' : status === 'rejected' ? 'error' : 'warning';
                            const bgMap = { success: '#E8F5E9', error: '#FFEBEE', warning: '#FFF8E1' };
                            const textMap = { success: '#2e7d32', error: '#c62828', warning: '#e65100' };

                            return (
                                <Box sx={{ mt: 2, p: 2, borderRadius: 2.5, bgcolor: bgMap[statusColor], border: `1px solid ${textMap[statusColor]}33` }}>
                                    <Stack direction='row' alignItems='center' justifyContent='space-between' flexWrap='wrap' gap={1}>
                                        {/* Left: icon + amount + date */}
                                        <Stack direction='row' alignItems='center' spacing={1}>
                                            <EmojiEvents sx={{ color: '#1565c0', fontSize: 20 }} />
                                            <Box>
                                                <Typography fontSize={11} color='text.secondary' lineHeight={1.2}>Requested Amount</Typography>
                                                <Typography fontWeight={700} fontSize={18} lineHeight={1.2}>{req.reward_value} coins</Typography>
                                                <Typography fontSize={11} color='text.secondary' mt={0.3}>{fmt(req.reward_created_at)}</Typography>
                                            </Box>
                                        </Stack>
                                        {/* Right: status chip (only for approved/rejected) + buttons */}
                                        <Stack alignItems='flex-end' spacing={1}>
                                            {(status === 'approved' || status === 'rejected') && (
                                                <Chip
                                                    label={status} size='small' color={statusColor} variant='filled'
                                                    sx={{ textTransform: 'capitalize', fontWeight: 700, fontSize: 12 }}
                                                />
                                            )}
                                            {status === 'request' && (
                                                <Stack direction='row' spacing={1}>
                                                    <Button
                                                        size='small' variant='contained' color='success'
                                                        disabled={actioning}
                                                        onClick={() => setConfirmAccept(true)}
                                                        sx={{ borderRadius: 2, fontWeight: 600, fontSize: 12, textTransform: 'none', px: 1.5 }}
                                                    >
                                                        Approve
                                                    </Button>
                                                    <Button
                                                        size='small' variant='outlined' color='error'
                                                        disabled={actioning}
                                                        onClick={() => setConfirmRevoke(true)}
                                                        sx={{ borderRadius: 2, fontWeight: 600, fontSize: 12, textTransform: 'none', px: 1.5 }}
                                                    >
                                                        Reject
                                                    </Button>
                                                </Stack>
                                            )}
                                        </Stack>
                                    </Stack>
                                </Box>
                            );
                        })() : (
                            <Box sx={{ mt: 2, p: 2, borderRadius: 2, bgcolor: '#f5f5f5', textAlign: 'center' }}>
                                <Typography fontSize={13} color='text.secondary'>No redemption request yet</Typography>
                            </Box>
                        )}
                    </CardContent>
                </Card>

                <Grid container spacing={2}>
                    {/* Info */}
                    <Grid item xs={12} md={6}>
                        <Card sx={{ borderRadius: 3, height: '100%' }}>
                            <CardContent>
                                <Typography fontWeight={700} fontSize={14} color='text.secondary' mb={1}>PERSONAL INFO</Typography>
                                <Divider sx={{ mb: 1 }} />
                                <InfoRow icon={<Person fontSize='small' />} label='Referrer ID' value={referrerId} />
                                <Divider />
                                <InfoRow icon={<Tag fontSize='small' />} label='Student ID' value={studentId} />
                                <Divider />
                                <InfoRow icon={<Phone fontSize='small' />} label='Mobile' value={r.mobile} />
                                <Divider />
                                <InfoRow icon={<Cake fontSize='small' />} label='Date of Birth' value={fmt(r.birth)} />
                                <Divider />
                                <InfoRow icon={<Wc fontSize='small' />} label='Gender' value={r.gender} />
                                <Divider />
                                <InfoRow icon={<CalendarMonth fontSize='small' />} label='Joined On' value={fmt(r.joindate)} />
                                <InfoRow icon={<CreditCard fontSize='small' />} label='UPI ID' value={r.upi_id} />
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Add Coins */}
                    <Grid item xs={12} md={6}>
                        <Card sx={{ borderRadius: 3, height: '100%' }}>
                            <CardContent>
                                <Stack direction='row' alignItems='center' spacing={1} mb={1}>
                                    <EmojiEvents color='warning' />
                                    <Typography fontWeight={700} fontSize={14} color='text.secondary'>ADD COINS</Typography>
                                </Stack>
                                <Divider sx={{ mb: 2 }} />
                                <TextField
                                    fullWidth type='number' label='Enter coin amount' size='small'
                                    value={coins} onChange={(e) => setCoins(e.target.value)}
                                    inputProps={{ min: 1 }}
                                    InputProps={{ startAdornment: (<InputAdornment position='start'><EmojiEvents fontSize='small' sx={{ color: '#FFB300' }} /></InputAdornment>) }}
                                    onKeyUp={(e) => e.key === 'Enter' && handleAddClick()}
                                    sx={{ mb: 2 }}
                                />
                                <Button
                                    variant='contained' color='warning' fullWidth size='large'
                                    startIcon={adding ? <CircularProgress size={18} color='inherit' /> : <Add />}
                                    onClick={handleAddClick}
                                    disabled={adding || !coins}
                                    sx={{ borderRadius: 2, fontWeight: 600 }}
                                >
                                    {adding ? 'Adding...' : 'Add Coins'}
                                </Button>
                                <Box sx={{ mt: 2.5, p: 1.5, borderRadius: 2, bgcolor: '#FFFDE7', border: '1px dashed #FFB300', textAlign: 'center' }}>
                                    <Typography fontSize={12} color='text.secondary'>Current Balance</Typography>
                                    <Stack direction='row' alignItems='center' justifyContent='center' spacing={0.5}>
                                        <EmojiEvents sx={{ color: '#FFB300', fontSize: 20 }} />
                                        <Typography fontWeight={700} fontSize={20}>{balanceSummary?.balance ?? totalCoins}</Typography>
                                        <Typography fontSize={12} color='text.secondary'>coins</Typography>
                                    </Stack>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Rewards History Table */}
                <Card sx={{ borderRadius: 3, mt: 2 }}>
                    <CardContent>
                        <Typography fontWeight={700} fontSize={14} color='text.secondary' mb={1}>REWARD HISTORY</Typography>
                        <Divider sx={{ mb: 1.5 }} />

                        {/* Balance Summary */}
                        {balanceSummary && (
                            <Grid container spacing={1.5} mb={2}>
                                {[
                                    { label: 'Total Credit', value: balanceSummary.total_credit, color: '#E8F5E9', text: '#2e7d32' },
                                    { label: 'Total Debit', value: balanceSummary.total_debit, color: '#FFEBEE', text: '#c62828' },
                                    { label: 'Balance', value: balanceSummary.balance, color: '#E3F2FD', text: '#1565c0' },
                                ].map(({ label, value, color, text }) => (
                                    <Grid item xs={4} key={label}>
                                        <Box sx={{ bgcolor: color, borderRadius: 2, p: 1.5, textAlign: 'center' }}>
                                            <Typography fontSize={11} color={text} fontWeight={600}>{label}</Typography>
                                            <Stack direction='row' alignItems='center' justifyContent='center' spacing={0.5}>
                                                <EmojiEvents sx={{ color: text, fontSize: 16 }} />
                                                <Typography fontWeight={700} fontSize={18} color={text}>{value}</Typography>
                                            </Stack>
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>
                        )}

                        <TableContainer>
                            <Table size='small'>
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                        <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>#</TableCell>
                                        <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>From</TableCell>
                                        <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Value</TableCell>
                                        <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Type</TableCell>
                                        <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Date</TableCell>
                                        <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Action</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rewardsLoading ? (
                                        <TableRow>
                                            <TableCell colSpan={6} align='center' sx={{ py: 3 }}><CircularProgress size={24} /></TableCell>
                                        </TableRow>
                                    ) : rewards.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={6} align='center' sx={{ py: 3, color: 'gray', fontSize: 13 }}>No rewards found</TableCell>
                                        </TableRow>
                                    ) : rewards.map((row, i) => (
                                        <TableRow key={i} hover>
                                            <TableCell sx={{ fontSize: 13, color: 'gray' }}>{i + 1}</TableCell>
                                            <TableCell sx={{ fontSize: 13 }}>{row.student_name || row.reward_from}</TableCell>
                                            <TableCell sx={{ fontSize: 13 }}>
                                                <Chip
                                                    icon={<EmojiEvents sx={{ fontSize: '14px !important', color: '#FFB300 !important' }} />}
                                                    label={row.reward_value} size='small'
                                                    sx={{ bgcolor: '#FFFDE7', fontWeight: 600, fontSize: 12 }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={row.reward_type} size='small'
                                                    color={row.reward_type === 'credit' ? 'success' : 'error'}
                                                    variant='outlined'
                                                    sx={{ textTransform: 'capitalize', fontSize: 12 }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ fontSize: 13, color: 'gray' }}>{fmt(row.reward_created_at)}</TableCell>
                                            <TableCell>
                                                <IconButton size='small' color='error' disabled={deleting} onClick={() => handleDeleteClick(row.rewardid)}>
                                                    <Delete fontSize='small' />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        {totalPages > 1 && (
                            <Stack alignItems='center' mt={2}>
                                <Pagination
                                    count={totalPages}
                                    page={page}
                                    onChange={(_, val) => setPage(val)}
                                    color='primary'
                                    size='small'
                                    shape='rounded'
                                />
                            </Stack>
                        )}
                    </CardContent>
                </Card>
            </Container>

            {/* Confirm Add Dialog */}
            <Dialog open={confirmAdd} onClose={() => setConfirmAdd(false)} maxWidth='xs' fullWidth>
                <DialogTitle sx={{ fontWeight: 700 }}>Confirm Add Coins</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to add <strong>{coins} coins</strong> to <strong>{r?.name}</strong>?
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setConfirmAdd(false)} color='inherit'>Cancel</Button>
                    <Button onClick={handleConfirmAdd} variant='contained' color='warning' sx={{ fontWeight: 600 }}>
                        Yes, Add
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Confirm Delete Dialog */}
            <Dialog open={confirmDelete.open} onClose={() => setConfirmDelete({ open: false, rewardid: null })} maxWidth='xs' fullWidth>
                <DialogTitle sx={{ fontWeight: 700, color: 'error.main' }}>Delete Reward</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this reward? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setConfirmDelete({ open: false, rewardid: null })} color='inherit'>Cancel</Button>
                    <Button onClick={handleConfirmDelete} variant='contained' color='error' sx={{ fontWeight: 600 }}>
                        Yes, Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Confirm Redeem Dialog */}
            <Dialog open={confirmRedeem} onClose={() => setConfirmRedeem(false)} maxWidth='xs' fullWidth>
                <DialogTitle sx={{ fontWeight: 700 }}>Confirm Redemption Request</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Submit a redemption request for <strong>{redeemAmount} coins</strong> from <strong>{r?.name}</strong>?
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setConfirmRedeem(false)} color='inherit'>Cancel</Button>
                    <Button onClick={handleConfirmRedeem} variant='contained' color='primary' sx={{ fontWeight: 600 }}>
                        Yes, Submit
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Confirm Accept Dialog */}
            <Dialog open={confirmAccept} onClose={() => setConfirmAccept(false)} maxWidth='xs' fullWidth>
                <DialogTitle sx={{ fontWeight: 700, color: 'success.main' }}>Approve Redemption</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to <strong>approve</strong> this redemption request of{' '}
                        <strong>{redeemRequest?.reward_value} coins</strong>?
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setConfirmAccept(false)} color='inherit'>Cancel</Button>
                    <Button onClick={handleAccept} variant='contained' color='success' disabled={actioning} sx={{ fontWeight: 600 }}>
                        Yes, Approve
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Confirm Revoke Dialog */}
            <Dialog open={confirmRevoke} onClose={() => setConfirmRevoke(false)} maxWidth='xs' fullWidth>
                <DialogTitle sx={{ fontWeight: 700, color: 'error.main' }}>Reject Redemption</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to <strong>reject</strong> this redemption request of{' '}
                        <strong>{redeemRequest?.reward_value} coins</strong>? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setConfirmRevoke(false)} color='inherit'>Cancel</Button>
                    <Button onClick={handleRevoke} variant='contained' color='error' disabled={actioning} sx={{ fontWeight: 600 }}>
                        Yes, Reject
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
